import React from "react";
import { ProgressBar, Button } from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { forecastActions } from "../../store/forecast-slice";
import {
  getOnboardingProgressMessage,
  shouldShowOnboardingProgress,
} from "../../utils/onboardingProgressBarUtils";
import classes from "./OnboardingProgressBanner.module.css";

/*
 * Same progress-slot rules as main-branch Header.jsx. The old header chrome
 * (logo/avatar) stays gone. After onboarding is completed this renders null,
 * except location-switch and Incoming Inventory — those still show while
 * their jobs run, then hide when done (same as Forecast).
 *
 * Main branch showed the bar in one exclusive slot:
 *   - Location switch in progress → location-switch bar (highest priority)
 *   - Incoming Inventory page + ongoingJobStatus → incoming-inventory bar
 *   - Historical authorized + historicalJobStatusObject.status === true
 *     → percentage bar + Refresh
 *   - Historical authorized but that job is not running → nothing
 *   - Not historical + shouldShowOnboardingProgress → 7/60 animated bar
 *
 * Refresh data sets forecast.shouldRefresh so Forecasting re-runs
 * initForecastingMain(). It does not start a new Shopify sync.
 */
/*
 * Optional end slot is the pin tip, on the right of whichever bar is
 * actually in this slot: onboarding, historical, location-switch, or
 * Incoming Inventory.
 *
 * With no slot, the markup is the same centered bar as before.
 */
const ProgressStrip = ({ children, endSlot }) => {
  const bannerClass = endSlot
    ? `${classes.banner} ${classes.bannerWithEnd}`
    : classes.banner;

  return (
    <div className={bannerClass} role={endSlot ? undefined : "status"}>
      {endSlot ? (
        <div className={classes.cluster} role="status">
          {children}
        </div>
      ) : (
        children
      )}
      {endSlot ? <div className={classes.endSlot}>{endSlot}</div> : null}
    </div>
  );
};

const OnboardingProgressBanner = ({ endSlot = null }) => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const {
    onboardingSyncJobStatus,
    historicalJobStatusObject,
    jobProgressPercentage,
    isOnboardingCompleted,
    ongoingJobStatus,
    incomingInventoryStatus7days,
    incomingInventoryStatus60days,
    locationSwitchInProgress,
    locationSwitchName,
    sync7DaysStatusRaw,
    sync60DaysStatusRaw,
  } = useSelector((state) => state.jobstatus);
  const currentActiveRoute = useSelector((state) => state.ui.currentActiveRoute);

  const handleRefresh = () => {
    dispatch(forecastActions.triggerForecastRefresh());
  };

  const progressArgs = {
    userDetails,
    onboardingSyncJobStatus,
    historicalJobStatusObject,
    sync7DaysStatusRaw,
    sync60DaysStatusRaw,
    currentActiveRoute,
  };

  const progressMessage = getOnboardingProgressMessage(progressArgs);
  const showProgress =
    shouldShowOnboardingProgress(progressArgs) && progressMessage !== null;
  const isHistoricalAuthorized =
    userDetails?.isAuthorizeHistoricalOrders === true;
  const isHistoricalRunning =
    isHistoricalAuthorized && historicalJobStatusObject?.status === true;
  const showIncomingInventoryProgress =
    currentActiveRoute === "incomingInventoryPage" &&
    ongoingJobStatus === true;

  const getIncomingInventoryProgressMessage = () => {
    if (
      isOnboardingCompleted === true &&
      incomingInventoryStatus7days &&
      incomingInventoryStatus60days
    ) {
      return "We are importing 'Incoming Inventory'.";
    }
    if (incomingInventoryStatus7days && incomingInventoryStatus60days) {
      return "We are now pulling incoming inventory for rest of the products";
    }
    if (!incomingInventoryStatus7days) {
      return "Importing incoming inventory of products which had sales in the last 7 days.";
    }
    if (incomingInventoryStatus7days) {
      return "Imported 'Incoming inventory' of products which had sales in the last 7 days. Currently, the app is getting the next.";
    }
    return "We are importing 'Incoming Inventory'.";
  };

  /*
   * Location switch uses the same slot the old Header did and wins over
   * every other bar. Show while locationSwitchInProgress is true, hide
   * when the switch finishes. Must run before the isOnboardingCompleted
   * early return — onboarded merchants still switch locations.
   */
  if (locationSwitchInProgress) {
    return (
      <ProgressStrip endSlot={endSlot}>
        <div className={`progress-bar progress-bar-for animate ${classes.bar}`}>
          <span className="pspan"></span>
          <div className="pspantext">
            {locationSwitchName
              ? `We are generating forecast report for ${locationSwitchName} location.`
              : "We are generating forecast report for all locations."}
          </div>
        </div>
      </ProgressStrip>
    );
  }

  /*
   * Incoming Inventory uses the same slot the old Header did: show while
   * ongoingJobStatus is true, hide when the job finishes. This must run
   * before the isOnboardingCompleted early return — onboarded merchants
   * still need this bar when they import POs.
   */
  if (showIncomingInventoryProgress) {
    return (
      <ProgressStrip endSlot={endSlot}>
        <div className={classes.bar}>
          <ProgressBar progress={10} />
          <div className="pspantext">
            {getIncomingInventoryProgressMessage()}
          </div>
        </div>
      </ProgressStrip>
    );
  }

  /*
   * After onboarding is done, show nothing — no header and no progress strip.
   * isOnboardingCompleted covers shops whose backend onboarding_status stays
   * stuck on PROCESSING after the jobs have actually finished.
   */
  if (isOnboardingCompleted) {
    return null;
  }

  const refreshButton = (
    <div className={classes.refreshButton}>
      <Button variant="primary" onClick={handleRefresh}>
        Refresh data
      </Button>
    </div>
  );

  /*
   * Match main Header's exclusive slot. Historical shops must not fall
   * through to the 7/60 bar — that is what made the strip appear after
   * those merchants were already onboarded.
   */
  if (isHistoricalAuthorized) {
    if (!isHistoricalRunning) {
      return null;
    }
    return (
      <ProgressStrip endSlot={endSlot}>
        <div className={classes.bar}>
          <ProgressBar progress={jobProgressPercentage} />
          <div className="pspantext">{historicalJobStatusObject?.message}</div>
        </div>
        {refreshButton}
      </ProgressStrip>
    );
  }

  if (!showProgress) {
    return null;
  }

  const messageLines = progressMessage.split("\n");

  return (
    <ProgressStrip endSlot={endSlot}>
      <div className={`progress-bar progress-bar-for animate ${classes.bar}`}>
        <span className="pspan"></span>
        <div className="pspantext">
          {messageLines.map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < messageLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      </div>
      {refreshButton}
    </ProgressStrip>
  );
};

export default OnboardingProgressBanner;
